import React, { useContext, useEffect, useMemo, useState } from 'react';
import { TranslationKey } from './../../../../../../types/translation/TranslationKey';
import { Divider, Grid } from '@mui/material';
import { t, translate } from '../../../../../../types/translation/Translator';
import {
  ProductTransaction,
  ProductTransactionParentType,
  ProductTransactionProcessType,
  ProductTransactionStatus,
  ProductTransactionType,
  productTransactionTypeToCustomFieldEntitySubType,
} from '../../../../../../types/productTransaction';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { LotContext } from '../../../../../../context/LotContext';
import { BinContext } from '../../../../../../context/BinContext';
import { useMutation } from '@apollo/client';
import {
  ProcessProductTransactionsResponse,
  ProcessProductTransactionsVariables,
  ProductTransactionMutations,
  UpdateProductTransactionCustomFieldsResponse,
  UpdateProductTransactionCustomFieldsVariables,
  RollbackProductTransactionsResponse,
  RollbackProductTransactionsVariables,
  ProcessProductTransactionForAssetResponse,
  ProcessProductTransactionForAssetVariables,
  CancelProductTransactionResponse,
  CancelProductTransactionVariables,
  UpdateProductTransactionVariablesResponse,
  UpdateProductTransactionVariables,
} from '../../../../../../graphql/productTransaction.graphql';
import { ProductContext } from '../../../../../../context/ProductContext';
import { HandlingUnitContext } from '../../../../../../context/HandlingUnitContext';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ProductTransactionContext } from '../../../../../../context/ProductTransactionContext';
import { StockLocationRoleAssignmentContext } from '../../../../../../context/StockLocationRoleAssignmentContext';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { CustomFieldEntityType } from '../../../../../../types/custom_field/customField';
import ModalPane from '../../../../../../VentoryUI/components/common/Modal/components/ModalPane';
import { FooterButton } from '../../../../../../VentoryUI/components/common/Button';
import { StockLocationContext } from '../../../../../../context/StockLocationContext';
import { ContainerContext } from '../../../../../../context/ContainerContext';
import { FlexPane } from '../../../../../../VentoryUI/components/common/FlexPane/FlexPane';
import Table from '../../../../../components/Table';
import { determineProduct, getAllChildProductTransactions } from '../../../../../../util/containers.util';
import { productTransactionTableHeaders } from '../../../../Common/ProductTransactionTable';
import { ContainerTypeContext } from '../../../../../../context/ContainerTypeContext';
import { UserContext } from '../../../../../../context/UserContext';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { StockLocationRole } from '../../../../../../types/stockLocationRoleAssignment';
import ProcessOrderTransactionHeader from './Views/ProcessOrderTransactionHeader';
import ProcessOrderTransactionBody from './Views/ProcessOrderTransactionBody';
import { productTransactionReasonForFailureToString } from '../../../../../../util/error.util';
import LoadingPackage from '../../../../Common/LoadingPackage';
import { Company } from '../../../../../../types/company';
import GenerateBarcodeButton from '../../../../../components/Button/templates/GenerateBarcodeButton';
import { BarcodeExportEntityType } from '../../../../../../types/barcode_export_configuration/barcodeExportConfiguration';
import { Button, ButtonVariant, CancelButton } from '../../../../../components/Button';
import { Unilever } from '../../../../../../types/customer/DHL/Unilever/Unilever';
import { UnitOfMeasure } from '../../../../../../types/uom';
import { TableSettingsContext } from '../../../../../../context/TableSettingsContext';
import { EntityType } from '../../../../../../types/comment';
import { Elteros } from '../../../../../../types/customer/Elteros/Elteros';

interface ProcessOrderTransactionInfoPaneProps {
  input: ProductTransaction;
  setError: (error: string) => void;
  onClose: () => void;
  disabled?: boolean;
  parentInfo?: boolean;
}

export default function ProcessOrderTransactionInfoPane({
  input,
  setError,
  onClose,
  disabled,
  parentInfo = false,
}: ProcessOrderTransactionInfoPaneProps) {
  dayjs.extend(customParseFormat);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { lots } = useContext(LotContext);
  const { bins, binsLoading } = useContext(BinContext);
  const { containers, setContainers, containersLoading } = useContext(ContainerContext);
  const { containerTypes } = useContext(ContainerTypeContext);
  const { products } = useContext(ProductContext);
  const { handlingUnits } = useContext(HandlingUnitContext);
  const { productTransactions, setProductTransactions, productTransactionsLoading } =
    useContext(ProductTransactionContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);
  const { customFields } = useContext(CustomFieldContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { companyUsers, currentUser, locale } = useContext(UserContext);
  const { currentCompany } = useContext(CompanyContext);
  const { getTableSettings } = useContext(TableSettingsContext);

  const productTransactionTableSettings = getTableSettings(
    EntityType.productTransaction,
    currentCompany.id,
    EntityType.order,
  );

  const [transaction, setTransaction] = useState<ProductTransaction>(new ProductTransaction(input));
  const [userId, setUserId] = useState<string | undefined>();

  const pmd = productMasterData.get(transaction.product.pmdId || '');
  const container = containers.get(transaction.product.containerId || '');
  if (!pmd && !container) return null;

  const containerTransaction = productTransactions.get(transaction.containerTransactionId || 'NONE');

  const scanRequired = Company.hasScanRequired(currentCompany, currentUser || undefined);

  useEffect(() => {
    if (containerTransaction?.product.toBinId) {
      setTransaction(transaction => transaction.withToBinId(pmd, containerTransaction.product.toBinId, true));
    }
  }, []);

  const [process, { loading }] = useMutation<ProcessProductTransactionsResponse, ProcessProductTransactionsVariables>(
    ProductTransactionMutations.process,
    {
      onCompleted: res => {
        const transaction = res.processProductTransaction[0];
        productTransactions.set(transaction.id, new ProductTransaction(transaction));
        setProductTransactions(new Map(productTransactions));
        onClose();
      },
      onError: err => setError(productTransactionReasonForFailureToString(err.message)),
    },
  );

  const [processAsset, { loading: processAssetLoading }] = useMutation<
    ProcessProductTransactionForAssetResponse,
    ProcessProductTransactionForAssetVariables
  >(ProductTransactionMutations.processAsset, {
    onCompleted: res => {
      const transaction = res.processProductTransactionForAsset;
      productTransactions.set(transaction.id, new ProductTransaction(transaction));
      setProductTransactions(new Map(productTransactions));
      onClose();
    },
    onError: err => setError(productTransactionReasonForFailureToString(err.message)),
  });

  const [cancel, { loading: cancelLoading }] = useMutation<
    CancelProductTransactionResponse,
    CancelProductTransactionVariables
  >(ProductTransactionMutations.cancel, {
    onCompleted: res => {
      res.cancelProductTransaction.forEach(transaction =>
        productTransactions.set(transaction.id, new ProductTransaction(transaction)),
      );
      setProductTransactions(new Map(productTransactions));
      onClose();
    },
    onError: err => setError(productTransactionReasonForFailureToString(err.message)),
  });

  const handleProcess = async () => {
    try {
      if (transaction.processType === ProductTransactionProcessType.asset) {
        await processAsset({
          variables: {
            productTransaction: transaction.forUpdate(),
            userId: userId,
          },
        });
      } else {
        await process({
          variables: {
            productTransactions: [transaction.forUpdate()],
          },
        });
      }
    } catch (e) {
      setError(String(e));
    }
  };

  const [update, { loading: updateLoading }] = useMutation<
    UpdateProductTransactionCustomFieldsResponse,
    UpdateProductTransactionCustomFieldsVariables
  >(ProductTransactionMutations.updateCustomFields, {
    onCompleted: res => {
      const transaction = res.updateProductTransactionCustomFields[0];
      productTransactions.set(transaction.id, new ProductTransaction(transaction));
      setProductTransactions(new Map(productTransactions));
      onClose();
    },
    onError: err => setError(productTransactionReasonForFailureToString(err.message)),
  });

  const [updateTransaction, { loading: updateTransactionLoading }] = useMutation<
    UpdateProductTransactionVariablesResponse,
    UpdateProductTransactionVariables
  >(ProductTransactionMutations.updateProductTransaction, {
    onCompleted: res => {
      const transaction = res.updateProductTransaction[0];
      productTransactions.set(transaction.id, new ProductTransaction(transaction));
      setProductTransactions(new Map(productTransactions));
      onClose();
    },
    onError: err => setError(productTransactionReasonForFailureToString(err.message)),
  });

  const handleUpdate = async () => {
    try {
      await update({ variables: { productTransactions: [transaction.forUpdate()] } });
    } catch (e) {
      setError(String(e));
    }
  };

  const [rollback, { loading: rollbackLoading }] = useMutation<
    RollbackProductTransactionsResponse,
    RollbackProductTransactionsVariables
  >(ProductTransactionMutations.rollback, {
    onCompleted: res => {
      const transaction = new ProductTransaction(res.rollbackProductTransaction[0]);
      productTransactions.set(transaction.id, transaction);
      setProductTransactions(new Map(productTransactions));
      onClose();
    },
    onError: err => setError(productTransactionReasonForFailureToString(err.message)),
  });

  const handleCancel = async () => {
    try {
      await cancel({
        variables: {
          ids: [transaction.id],
        },
      });
    } catch (e) {
      setError(String(e));
    }
  };

  const handleRollback = async () => {
    try {
      transaction.status = ProductTransactionStatus.rollback;

      await rollback({
        variables: {
          productTransactions: [transaction.forUpdate()],
        },
      });
    } catch (e) {
      setError(String(e));
    }
  };

  const relevantCustomFields = useMemo(() => {
    const productTransactionEntityTypes = new Set<CustomFieldEntityType>([CustomFieldEntityType.productTransaction]);
    if (transaction.product.containerId) productTransactionEntityTypes.add(CustomFieldEntityType.containerTransaction);

    return [...customFields.values()].filter(
      cf =>
        (!cf.generated &&
          !cf.hidden &&
          productTransactionEntityTypes.has(cf.entityType) &&
          cf.entitySubtype === productTransactionTypeToCustomFieldEntitySubType(transaction.type)) ||
        cf.entityType === CustomFieldEntityType.product,
    );
  }, [customFields, transaction.product.containerId, transaction.type]);

  const stockLocationId =
    transaction.type === ProductTransactionType.inbound || transaction.type === ProductTransactionType.replenish
      ? transaction.product.toStockLocationId
      : transaction.product.fromStockLocationId;
  if (!stockLocationId) return null;

  disabled ||=
    !hasStockLocationRole(transaction.companyId, stockLocationId, StockLocationRole.STOCK_LOCATION_USER) ||
    binsLoading ||
    containersLoading;

  const isViewer = !hasStockLocationRole(currentCompany.id, stockLocationId, StockLocationRole.STOCK_LOCATION_USER);

  // This should be ripped out & have its own component
  const buttons = useMemo(() => {
    if (isViewer) return [];

    const shown: FooterButton[] = [
      {
        override: (
          <Button
            variant={ButtonVariant.secondary}
            onClick={transaction.status === ProductTransactionStatus.created ? handleCancel : handleRollback}
            text={
              transaction.status !== ProductTransactionStatus.created
                ? t(TranslationKey.rollbackTransaction).singular.label
                : t(TranslationKey.cancelTransaction).singular.label
            }
            loading={cancelLoading || rollbackLoading}
            disabled={
              loading ||
              rollbackLoading ||
              processAssetLoading ||
              transaction.status === ProductTransactionStatus.cancelled ||
              transaction.status === ProductTransactionStatus.failed ||
              transaction.status === ProductTransactionStatus.rollback ||
              rollbackLoading ||
              transaction.processType === ProductTransactionProcessType.asset ||
              (transaction.status !== ProductTransactionStatus.created && !!transaction.containerTransactionId)
            }
          />
        ),
        align: 'left',
        onClick: () => {},
      },
      {
        override: <CancelButton onClick={onClose} />,
        onClick: () => {},
      },
    ];

    if (transaction.status === ProductTransactionStatus.processed) {
      shown.push({
        align: 'left',
        text: '',
        onClick: () => {},
        override: (
          <GenerateBarcodeButton entityIds={[transaction.id]} entityType={BarcodeExportEntityType.productTransaction} />
        ),
      });
    }

    if (
      transaction.status === ProductTransactionStatus.processed &&
      transaction.companyId === Elteros.companyId &&
      transaction.type === ProductTransactionType.outbound
    ) {
      shown.push({
        align: 'right',
        text: '',
        loading: updateTransactionLoading || updateLoading,
        onClick: () => {},
        override: (
          <Button
            text={translate({ key: TranslationKey.confirm })}
            loading={updateTransactionLoading || updateLoading}
            onClick={async () => {
              transaction.status = ProductTransactionStatus.confirmed;
              await updateTransaction({ variables: { productTransactions: [transaction.forUpdate()] } });
            }}
          />
        ),
      });
    }

    if (!disabled) {
      if (relevantCustomFields.length) {
        shown.push({
          override: (
            <Button
              variant={ButtonVariant.primary}
              onClick={handleUpdate}
              text={translate({ key: TranslationKey.update })}
              loading={updateLoading}
              disabled={cancelLoading || rollbackLoading || loading}
            />
          ),
          align: 'left',
          onClick: () => {},
        });
      }

      const isDisabled = () => {
        if (disabled) return true;
        if (scanRequired) return true;

        if (
          Unilever.companyIds.includes(transaction.companyId) &&
          transaction.type === ProductTransactionType.outbound &&
          transaction.product.fromContainerId &&
          transaction.product.lotId
        ) {
          return false;
        }

        if (
          !transaction.product.processedQuantity ||
          transaction.product.processedQuantity === 0 ||
          isNaN(transaction.product.processedQuantity)
        ) {
          return true;
        }

        if (transaction.product.processedQuantity > transaction.product.quantity) return true;
        if (transaction.processType === ProductTransactionProcessType.asset) return false;

        if (transaction.type === ProductTransactionType.inbound) {
          for (const customField of relevantCustomFields.filter(
            cf => cf.entityType === CustomFieldEntityType.product,
          )) {
            if (!customField.mandatory) continue;
          }
        }

        if (pmd?.serialManaged && !transaction.product.serialNbr) return true;
        if (pmd?.lpnManaged && !transaction.product.lpn) return true;
        if (pmd?.lotManaged && !transaction.product.lotId) return true;
        if (
          (transaction.type === ProductTransactionType.inbound || transaction.type === ProductTransactionType.move) &&
          !transaction.product.toBinId
        ) {
          return true;
        }

        if (
          (transaction.type === ProductTransactionType.outbound || transaction.type === ProductTransactionType.move) &&
          !transaction.product.fromBinId
        ) {
          return true;
        }

        if (!new Set([ProductTransactionStatus.created, ProductTransactionStatus.picked]).has(transaction.status)) {
          return true;
        }

        if (transaction.product.containerId) return false;

        if (
          transaction.type === ProductTransactionType.outbound ||
          (transaction.type === ProductTransactionType.move && transaction.status !== ProductTransactionStatus.picked)
        ) {
          const existing = determineProduct(transaction, products, customFields, handlingUnits);
          if (!existing) return true;

          const originatesFromHandlingUnitProduct =
            !!transaction.product.handlingUnitId && existing.productMasterDataId !== transaction.product.pmdId;
          if (originatesFromHandlingUnitProduct) return false;

          const quantity = parseFloat(UnitOfMeasure.toQuantity(pmd?.unitOfMeasure, existing.unitQuantity));
          if (quantity < transaction.product.processedQuantity) {
            setTransaction(transaction => transaction.withProcessedQuantity(quantity));
          } else if (quantity > transaction.product.processedQuantity && quantity < transaction.product.quantity) {
            setTransaction(transaction => transaction.withProcessedQuantity(quantity));
          }
        }

        return false;
      };

      if (
        transaction.status === ProductTransactionStatus.created ||
        transaction.status === ProductTransactionStatus.picked
      ) {
        shown.push({
          override: (
            <Button
              variant={ButtonVariant.primary}
              onClick={handleProcess}
              text={
                currentCompany.settings.featureToggles.orders.picking &&
                transaction.status === ProductTransactionStatus.created &&
                ProductTransaction.hasPicking(transaction)
                  ? t(TranslationKey.pick).singular.label
                  : t(TranslationKey.process).singular.label
              }
              loading={loading || processAssetLoading}
              disabled={isDisabled()}
            />
          ),
          align: 'right',
          onClick: () => {},
        });
      }
    }

    return shown;
  }, [disabled, transaction, loading, processAssetLoading, rollbackLoading, userId, cancelLoading]);

  const isLoading = binsLoading || containersLoading;
  return (
    <ModalPane footerButtons={buttons}>
      <FlexPane
        header={
          <Grid container>
            <ProcessOrderTransactionHeader
              transaction={transaction}
              stockLocationId={stockLocationId}
              container={container}
              productMasterData={pmd}
              showParentInfo={parentInfo}
              scanRequired={scanRequired}
              disabled={disabled}
            />
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            {isLoading ? (
              <Grid container justifyContent={'center'} alignContent={'center'}>
                <LoadingPackage />
              </Grid>
            ) : (
              <ProcessOrderTransactionBody
                transaction={transaction}
                setTransaction={setTransaction}
                container={container}
                productMasterData={pmd}
                userId={userId}
                setUserId={setUserId}
                disabled={disabled || scanRequired}
              />
            )}
          </Grid>
        }
        content={
          transaction.product.containerId ? (
            <Table<ProductTransaction>
              modal
              customPagination={5}
              title={'Container Contents'}
              items={getAllChildProductTransactions(transaction, productTransactions).filter(
                t => t.parentType !== ProductTransactionParentType.transaction,
              )}
              headers={productTransactionTableHeaders(
                productTransactionTableSettings,
                stockLocations,
                bins,
                productMasterData,
                lots,
                companyUsers,
                containers,
                containerTypes,
                locale,
              )}
            />
          ) : null
        }
      />
    </ModalPane>
  );
}
